/**
 * Chat Service
 * AIDLC Administrative Backend
 *
 * API calls for chat conversations and message management.
 * Maps to endpoints defined in the Architecture Document, Section 04.
 */

import apiClient from './api';
import type { ChatMessage, Conversation, CreatePromptPayload, PromptQueueItem } from '../types/chat';

const CHAT_PATH = '/chat';
const PROMPTS_PATH = '/prompts';

/**
 * Get all conversations (active + optionally archived)
 */
export async function getConversations(includeArchived = false): Promise<Conversation[]> {
  const params = includeArchived ? { includeArchived: 'true' } : {};
  const response = await apiClient.get<Conversation[]>(`${CHAT_PATH}/conversations`, { params });
  return response.data;
}

/**
 * Get messages for a conversation
 */
export async function getMessages(
  conversationId: string,
  limit = 50,
  before?: string
): Promise<ChatMessage[]> {
  const params: Record<string, string | number> = { limit };
  if (before) params.before = before;
  const response = await apiClient.get<ChatMessage[]>(
    `${CHAT_PATH}/conversations/${conversationId}/messages`,
    { params }
  );
  return response.data;
}

/**
 * Send a message in a conversation
 */
export async function sendMessage(
  conversationId: string,
  content: string
): Promise<ChatMessage> {
  const response = await apiClient.post<ChatMessage>(
    `${CHAT_PATH}/conversations/${conversationId}/messages`,
    { content }
  );
  return response.data;
}

/**
 * "Go Do It" -- compose prompt from conversation and queue it
 * This is the crown jewel action. Only Admin role can execute.
 */
export async function executeGoDoIt(
  conversationId: string,
  payload: CreatePromptPayload
): Promise<PromptQueueItem> {
  const response = await apiClient.post<PromptQueueItem>(
    `${CHAT_PATH}/conversations/${conversationId}/execute`,
    payload
  );
  return response.data;
}

/**
 * Get all prompts in the queue
 */
export async function getPromptQueue(
  status?: string
): Promise<PromptQueueItem[]> {
  const params = status ? { status } : {};
  const response = await apiClient.get<PromptQueueItem[]>(PROMPTS_PATH, { params });
  return response.data;
}

/**
 * Update prompt status (processing, completed, failed)
 */
export async function updatePromptStatus(
  promptId: string,
  status: string,
  result?: string
): Promise<PromptQueueItem> {
  const response = await apiClient.put<PromptQueueItem>(
    `${PROMPTS_PATH}/${promptId}/status`,
    { status, result }
  );
  return response.data;
}

export async function editPrompt(
  promptId: string,
  updates: { subject?: string; prompt?: string; priority?: string }
): Promise<PromptQueueItem> {
  const response = await apiClient.put<PromptQueueItem>(
    `${PROMPTS_PATH}/${promptId}`,
    updates
  );
  return response.data;
}

export async function cancelPrompt(promptId: string): Promise<void> {
  await apiClient.delete(`${PROMPTS_PATH}/${promptId}`);
}

/**
 * Clear all messages in a conversation (keeps conversation shell)
 */
export async function clearConversation(conversationId: string): Promise<void> {
  await apiClient.delete(`${CHAT_PATH}/conversations/${conversationId}/messages`);
}

/**
 * Delete a conversation entirely (messages + conversation)
 */
export async function deleteConversation(conversationId: string): Promise<void> {
  await apiClient.delete(`${CHAT_PATH}/conversations/${conversationId}`);
}

/**
 * Archive a conversation (hide from main list, preserve messages)
 */
export async function archiveConversation(conversationId: string): Promise<void> {
  await apiClient.put(`${CHAT_PATH}/conversations/${conversationId}/archive`);
}

/**
 * Unarchive a conversation (move back to main list)
 */
export async function unarchiveConversation(conversationId: string): Promise<void> {
  await apiClient.put(`${CHAT_PATH}/conversations/${conversationId}/unarchive`);
}
